/**
 * Client for the conversion path (fnd-bmi.1): /api/domain/connect.
 *
 * The one thing worth knowing here is how "not enabled yet" is handled. The
 * backend answers 501 CUSTOM_DOMAINS_DISABLED while the owner has not turned
 * the Cloudflare for SaaS integration on, and that response still carries the
 * DNS records a client would need. Axios treats 501 as an error, so every call
 * below unwraps it into a normal `{ enabled: false }` state instead: the page
 * then shows an honest "coming soon" panel rather than an error toast or a
 * form that silently goes nowhere.
 */

import api from './api';
import { DEFAULT_FALLBACK_ORIGIN, buildDnsRecords, type DnsRecord } from '../lib/dns';

export type CustomDomainStatus = 'pending_dns' | 'pending_ssl' | 'active' | 'failed';

export interface CustomDomainHostname {
  hostname: string;
  status: CustomDomainStatus;
  detail: string | null;
}

export interface CustomDomainState {
  /** False while the owner has not enabled Cloudflare for SaaS yet. */
  enabled: boolean;
  websiteId?: string;
  domain: string | null;
  status: CustomDomainStatus | null;
  detail: string | null;
  connectedAt: string | null;
  hostnames: CustomDomainHostname[];
  records: DnsRecord[];
  fallbackOrigin: string;
  /** True when we own the registrar side and wrote the records ourselves. */
  dnsManaged: boolean;
}

const EMPTY: CustomDomainState = {
  enabled: false,
  domain: null,
  status: null,
  detail: null,
  connectedAt: null,
  hostnames: [],
  records: [],
  fallbackOrigin: DEFAULT_FALLBACK_ORIGIN,
  dnsManaged: false,
};

function normalizeState(payload: unknown): CustomDomainState {
  const data = (payload ?? {}) as Partial<CustomDomainState>;
  return {
    ...EMPTY,
    ...data,
    enabled: data.enabled ?? false,
    hostnames: data.hostnames ?? [],
    records: data.records ?? [],
    fallbackOrigin: data.fallbackOrigin || DEFAULT_FALLBACK_ORIGIN,
  };
}

/**
 * Read an axios-style rejection without importing axios.
 *
 * `axios.isAxiosError` is the obvious call, but the app's test setup replaces
 * the axios module wholesale, so that helper is not always a function at
 * runtime. Duck-typing the response shape is both safer here and enough: all we
 * need is the status and the body.
 */
function httpResponseOf(error: unknown): { status?: number; data?: any } | null {
  if (!error || typeof error !== 'object') return null;
  const response = (error as { response?: unknown }).response;
  if (!response || typeof response !== 'object') return null;
  return response as { status?: number; data?: any };
}

/** A 501 is a state, not a failure — anything else is rethrown for the caller. */
function disabledStateOrRethrow(error: unknown, domain: string): CustomDomainState {
  const response = httpResponseOf(error);
  if (response?.status === 501) {
    const payload = response.data?.data;
    return normalizeState({
      ...payload,
      enabled: false,
      records: payload?.records ?? buildDnsRecords(domain),
    });
  }
  throw error;
}

/** Message the API can raise for the client to read, when there is one. */
export function customDomainErrorMessage(error: unknown): string | null {
  const message = httpResponseOf(error)?.data?.message;
  return typeof message === 'string' && message ? message : null;
}

export const customDomainService = {
  async status(websiteId?: string): Promise<CustomDomainState> {
    try {
      const response = await api.get('/api/domain/connect/status', {
        params: websiteId ? { websiteId } : undefined,
      });
      return normalizeState(response.data?.data);
    } catch (error) {
      return disabledStateOrRethrow(error, '');
    }
  },

  async connect(domain: string, websiteId?: string): Promise<CustomDomainState> {
    try {
      const response = await api.post('/api/domain/connect', {
        domain,
        ...(websiteId ? { websiteId } : {}),
      });
      return normalizeState(response.data?.data);
    } catch (error) {
      return disabledStateOrRethrow(error, domain);
    }
  },

  async disconnect(websiteId?: string): Promise<void> {
    await api.delete('/api/domain/connect', {
      data: websiteId ? { websiteId } : {},
    });
  },
};
